// §III · THE SEVEN LAYERS — ported VERBATIM from the Claude Design canon
// `R10 - §III - canon F3.html` (project dd7b95d4, data-canon="R10-§III-F3"). Layout =
// R9 reference, restored (styles/r10.css lp-*/r4-* block; §III CSS is unchanged F2→F3).
// Fixed brand copy is verbatim (title, intro, family legend, the how-to-read /
// where-it-leads notes). The chart, the per-layer value strips, the "now" values, and
// the where-you-are-now footer are LIVE from the recorded input.
//
// §III is SELF-MANAGED like §II: it renders its OWN artboard shell (the <section> with
// the §III runner + "iii · landscape" footer), so it is mounted WITHOUT the generic
// Artboard wrapper. It is board-driven (a `view`); the wizard→view adapter lands with
// Track B/C, exactly as for §II.
//
// F3 field behaviour, all from CD `page()`/`cards()`/`standingCopy()`:
//   • a layer value of null prints "—" (class nul), a real 0 prints "0" (class zero) —
//     zero is an answer, a dash is the absence of one.
//   • a layer empty on every stage keeps its CARD (dashes, no "now" row, data-
//     degradation) but draws NO curve — the 4+3 grid never re-packs (s164).
//   • the where-you-are-now block is dropped whole when 0 layers are touched, and the
//     footer collapses to two columns (lp-notes-2).
//   • the where-you-are-now prose is the engine's currentStanding → the canonical §III
//     phrase (standing.js); the amber layer name and the em forward clause are marked.

import { Fragment } from 'react';
import SevenLayersChart from '../SevenLayersChart.jsx';
import { LAYERS } from '../layersConfig.js';
import { standingState } from '../standing.js';

// meta.timestamp of the dataset snapshot; in prod this rides on the run. Parsed by
// field (NOT `new Date`) so the "DD · MM · YYYY" stamp is timezone-independent.
const SNAPSHOT_STAMP = '2026-07-29';
const fmtStamp = (iso) => {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d} · ${m} · ${y}`;
};

// Card top-bar fill — solid for solid layers, a repeating dash gradient for dashed ones
// (verbatim CD `bar` in cards()), so the bar mirrors the line's stroke style.
const barFill = (L) => {
  if (!L.dash) return L.color;
  const [on, off] = L.dash.split(' ').map(Number);
  return `repeating-linear-gradient(to right, ${L.color} 0 ${on}px, transparent ${on}px ${on + off}px)`;
};

// two degradation notes, chosen by DATA not by the touched map (verbatim CD `deg`)
const DEG_ALLNULL = 'слой пуст на всех N этапах → кривая не рисуется, карточка остаётся с прочерками, знаменатель падает';
const DEG_UNTOUCHED = 'слой не тронут → в интерпретацию не идёт, на чарте остаётся';

// value strip: null → "—" (nul), 0 → "0" (zero), ±n → "+n"/"-n"
const valClass = (x) => (x === null ? 'nul' : x > 0 ? 'pos' : x < 0 ? 'neg' : 'zero');
const valText = (x) => (x === null ? '—' : (x > 0 ? '+' : '') + x);

function Cards({ sliders, touched }) {
  return (
    <div className="lp-legend">
      {LAYERS.map((L) => {
        const vals = sliders.map((s) => s[L.key]);
        const nowV = vals[vals.length - 1];
        const allNull = vals.every((x) => x === null || x === undefined);
        const deg = allNull ? DEG_ALLNULL : touched && touched[L.key] === false ? DEG_UNTOUCHED : null;
        return (
          <div className="lp-leg" key={L.key} style={{ borderLeftColor: L.color }} {...(deg ? { 'data-degradation': deg } : {})}>
            <div className="r4-leg-topbar" data-field="layer_cards[i].top_bar" style={{ background: barFill(L) }} />
            <div className="r4-leg-name" data-field="layer_cards[i].layer_name">{L.name}</div>
            <div className="r4-leg-vals" data-field="layer_cards[i].values_series">
              {vals.map((x, si) => (
                <span className={`v ${valClass(x)}`} key={si}>{valText(x)}</span>
              ))}
            </div>
            {nowV === null ? null : (
              <div className="r4-leg-now" data-field="layer_cards[i].now_value">now · <b>{nowV > 0 ? '+' : ''}{nowV}</b></div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StandingProse({ standing }) {
  return (
    <>
      {standing.segments.map((s, i) =>
        s.amber ? (
          <span className="lp-note-amber" key={i}>{s.text}</span>
        ) : s.em ? (
          <em key={i}>{s.text}</em>
        ) : (
          <Fragment key={i}>{s.text}</Fragment>
        ),
      )}
    </>
  );
}

export default function SectionIII({ view }) {
  const { sliders, stages, id } = view;
  const N = stages.length;
  // ПРОЛЁТ ПРИХОДИТ ИЗ ШВА. Этот же расчёт когда-то стоял здесь целиком — и разошёлся с §I и
  // §II на паспорте с открытым началом (см. `viewIII`). Осталась одна ветка: рукописные борды
  // `III_BOARDS` поля `years` не несут, для них расчёт ниже сохранён дословно.
  const first = stages[0].openStart ? stages[0].endY : stages[0].startY;
  const last = stages[N - 1].endY;
  // cc-30: `datesIncomplete` deliberately excuses a missing end on the CURRENT stage — which is
  // exactly the stage whose end can be null (`toWizardInput`: the last stage ends at the
  // submit-time `nowY`, and `StageMeta.endY` is `number|null`). So the guard that was supposed
  // to catch this is what let it through: `null - first` printed "-2000 years on one map.", and
  // an absent endY printed "NaN years on one map." The span is a CLAIM; with no end date we do
  // not have it, so the line is dropped exactly as it is for incomplete dates. No-op on canon,
  // where every board's last stage carries an end.
  const spanKnown = Number.isFinite(first) && Number.isFinite(last);
  const years = view.years !== undefined ? view.years
    : ((view.datesIncomplete || !spanKnown) ? null : Math.round(last - first));
  const standing = standingState(view.standing); // null → block omitted, footer 2-col
  const touchedCount = view.standing ? view.standing.touchedCount : 0;

  return (
    <section
      className="artboard layers-page r4 r4-layers"
      data-screen-label={view.screen}
      data-run={view.source}
      data-canon="R10-§III-F3"
      data-stage-count={N}
      data-touched={touchedCount}
    >
      <div className="ab-paper" />
      <div className="ab-grain" />
      <header className="ab-runner">
        <span data-field="section_header_left"><span className="runner-roman">III</span>seven layers</span>
        <span className="runner-meta" data-field="section_header_right">life map · the reading</span>
      </header>
      <div className="lp-sheet">
        <div className="lp-hd">
          <div className="lp-roman">III</div>
          <h2 className="lp-title" data-field="page_title">
            <em>seven layers</em> <span className="lp-amber">of the same life.</span>
          </h2>
          <div className="lp-tag">
            <span data-field="top_meta_left">{N} stages · 7 layers</span>
            {years !== null && <em data-field="top_meta_right">{years} years on one map.</em>}
          </div>
        </div>

        <p className="lp-sub" data-field="intro_paragraph">
          what a single overall rating hides, seven layers reveal. this is the geometry of every
          dimension you cared about — <em>vitality, security, growth, authorship, wanting, being
          yourself, wanted to last</em> — across the recorded arc. <em>the chart speaks first. read
          it slowly.</em>
        </p>

        <div className="lp-chart-wrap">
          <div className="lp-chart-tag" data-field="chart_eyebrow">landscape · all of life · <em>7 layers</em></div>
          <div className="lp-chart-frame">
            <span className="r4-corner tl" />
            <span className="r4-corner tr" />
            <span className="r4-corner bl" />
            <span className="r4-corner br" />
            <SevenLayersChart sliders={sliders} stages={stages} gid={id} labelMode={view.labelMode} />
          </div>
        </div>

        <p className="r4-fam-legend" data-field="layer_legend">
          <span className="fam-amber">amber</span> — dynamic family (vitality, wanting).{' '}
          <span className="fam-moss">moss</span> — relational family (growth, being yourself).{' '}
          <span className="fam-paper">paper</span> — slow-arc family (security, authorship, wanted to last).{' '}
          <em>solid / dashed / dotted differentiates inside a family.</em>
        </p>

        <Cards sliders={sliders} touched={view.touched} />

        <div className={`lp-notes${standing ? '' : ' lp-notes-2'}`}>
          {standing && (
            <div>
              <div className="lp-note-k">where you are now</div>
              <p className="lp-note-v" data-field="footer_where_you_are_now"><StandingProse standing={standing} /></p>
            </div>
          )}
          <div>
            <div className="lp-note-k">how to read it</div>
            <p className="lp-note-v" data-field="footer_how_to_read">
              each curve is one dimension you cared about, scored once per stage. where curves
              cluster, life felt whole. where they spread, it didn't.
            </p>
          </div>
          <div>
            <div className="lp-note-k">where it leads</div>
            <p className="lp-note-v" data-field="footer_where_it_leads">one shape runs through all the stages. one line carries it. <em>→ the red line reads that.</em></p>
          </div>
        </div>
      </div>
      <footer className="ab-foot">
        <span data-field="footer_left">
          <span className="foot-em">life map</span> <span className="foot-faint">·</span> seven layers <span className="foot-faint">·</span> <span className="foot-amber" data-field="footer_timestamp">{fmtStamp(view.generatedAt || SNAPSHOT_STAMP)}</span>
        </span>
        <span className="foot-page" data-field="footer_right">iii · landscape</span>
      </footer>
    </section>
  );
}
